import { useApi } from '@/api';
import {
  ActivityCard,
  EntityName,
  KpiCard,
  Link,
  Page,
  PageSkeleton
} from '@/components';
import {
  CryptoIcon,
  EtherScanPictogram,
  Flex,
  Heading,
  LinkWrapper
} from '@blockanalitica/ui';
import { useParams } from 'react-router-dom';
import HistoricTabs from './HistoricTabs';
import HoldersDonutChart from './HoldersDonutChart';
import TopHolders from './TopHolders';

export default function TokensBasePage() {
  const { address } = useParams();

  const [data, error] = useApi({
    url: `/tokens/base/${address}/`,
    queryParams: {
      days_ago: 1
    }
  });

  if (error) {
    throw error;
  }

  if (data) {
    let variant: string;

    switch (data?.symbol) {
      case 'USDS':
        variant = 'usds';
        break;
      case 'sUSDS':
        variant = 'susds';
        break;
      default:
        variant = 'default';
        break;
    }

    return (
      <Page
        title={
          <EntityName
            symbol1={
              <CryptoIcon
                name={data.symbol}
                address={address}
                network="base"
                size="3"
              />
            }>
            <Heading size="1">
              <Flex gap="xs">
                {data.symbol}
                <a
                  href={`https://basescan.org/address/${address}`}
                  target="_blank"
                  rel="noreferrer">
                  <EtherScanPictogram size="0" />
                </a>
              </Flex>
            </Heading>
          </EntityName>
        }>
        <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          <KpiCard
            title="Circulating Supply"
            data={data}
            variant={variant}
            field="total_supply"
            options={{
              currency: {
                name: data.symbol,
                address: address,
                network: 'base'
              },
              notation: 'standard'
            }}
          />
        </Flex>

        <HistoricTabs address={address} />
        <Flex
          flexBasis="100%"
          gap={['m', 's']}
          flexDirection={['column', 'row']}>
          <KpiCard
            title="Market Cap"
            data={data}
            field="market_cap"
            options={{
              currency: 'USD',
              notation: 'standard'
            }}
          />
          <KpiCard
            title="Price"
            data={data}
            field="price"
            options={{
              currency: 'USD',
              notation: 'standard',
              valueOptions: {
                currencyValueOptions: {
                  formatCurrencyOptions: {
                    maximumFractionDigits: 4
                  }
                }
              }
            }}
          />
        </Flex>
        <ActivityCard
          url={`/tokens/base/${address}/activity/`}
          linkTo="activity"
        />
        <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          <Flex gap={['m', 's']} flexDirection="column" width="100%">
            <LinkWrapper
              to="holders"
              // @ts-ignore
              as={Link}
              variant="kpiCardLinkWithBlendMode">
              <KpiCard
                title="N° of Holders"
                data={data}
                field="holders"
                options={{ linkArrow: true }}
              />
            </LinkWrapper>
            <TopHolders address={address} flexBasis="100%" />
          </Flex>
          <Flex flexBasis="100%" flexDirection="column" alignItems="center">
            <Heading size="3">Holders Distribution</Heading>
            <HoldersDonutChart address={address} />
          </Flex>
        </Flex>
      </Page>
    );
  }
  return <PageSkeleton />;
}
